import { makeAutoObservable, runInAction } from "mobx";

import { ApiError } from "@mr-portfolio-api/api-error";

/**
 * Observable wrapper for one piece of server data. Each `load` aborts the previous request,
 * so only the latest response is applied.
 */
export class AsyncResource<TData> {
  public data: TData;
  public isLoading = false;
  public error: ApiError | null = null;
  public hasLoaded = false;

  private abortController: AbortController | null = null;

  public constructor(initialData: TData) {
    this.data = initialData;
    makeAutoObservable<AsyncResource<TData>, "abortController">(this, { abortController: false });
  }

  /** True until the first successful load or an error: use it for skeletons and initial spinners. */
  public get isPending(): boolean {
    const isPending = !this.hasLoaded && this.error === null;

    return isPending;
  }

  public async load(fetchData: (signal: AbortSignal) => Promise<TData>): Promise<void> {
    this.abortController?.abort();
    const abortController = new AbortController();
    this.abortController = abortController;
    this.isLoading = true;
    this.error = null;

    try {
      const loadedData = await fetchData(abortController.signal);

      runInAction(() => {
        if (!abortController.signal.aborted) {
          this.data = loadedData;
          this.hasLoaded = true;
        }
      });
    } catch (caughtError: unknown) {
      runInAction(() => {
        if (!abortController.signal.aborted) {
          this.error = caughtError instanceof ApiError ? caughtError : new ApiError(0, { title: String(caughtError) });
        }
      });
    } finally {
      runInAction(() => {
        if (!abortController.signal.aborted) {
          this.isLoading = false;
        }
      });
    }
  }

  public setData(data: TData): void {
    this.data = data;
  }

  public abort(): void {
    this.abortController?.abort();
    this.isLoading = false;
  }
}
